import { t } from "../lib/i18n";
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import EditTransactionModal from './EditTransactionModal';
import { addFinancialsChangeListener, removeFinancialsChangeListener, dispatchFinancialsChangeEvent } from '../financialsEventSystem';
import { showConfirm, showAlert } from '../utils/ModalService';


interface FinancialRecord {
    id?: string;
    transaction_type: string;
    description?: string;
    amount: number;
    due_date?: string;
    paid_date?: string;
    lease_id?: string;
    category: 'income' | 'expense';
}

interface FinancialsLedgerProps {
    propertyId: string;
}

const FinancialsLedger: React.FC<FinancialsLedgerProps> = ({ propertyId }) => {
    const [financials, setFinancials] = useState<{ income: FinancialRecord[], expenses: FinancialRecord[] }>({ income: [], expenses: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newTransaction, setNewTransaction] = useState<Partial<FinancialRecord>>({});
    const [transactionTypeCategory, setTransactionTypeCategory] = useState<'income' | 'expense'>('income');
    const [message, setMessage] = useState('');
    const [editingTransaction, setEditingTransaction] = useState<FinancialRecord | null>(null);

    const fetchFinancials = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/properties/${propertyId}/financials`);
            setFinancials(response.data);
        } catch (err) {
            console.error('Error fetching financials:', err);
            setError('Failed to load financial data.');
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        if (!propertyId) return;
        fetchFinancials();
        addFinancialsChangeListener(fetchFinancials);
        return () => {
            removeFinancialsChangeListener(fetchFinancials);
        };
    }, [propertyId, fetchFinancials]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewTransaction(prev => ({ ...prev, [name]: name === 'amount' ? parseFloat(value) || 0 : value }));
    };

    const handleAddTransaction = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        try {
            const transactionDataToSend = {
                ...newTransaction,
                category: transactionTypeCategory,
            };
            await api.post(`/properties/${propertyId}/financials`, transactionDataToSend);
            setMessage('Transaction added successfully!');
            setNewTransaction({});
            dispatchFinancialsChangeEvent();
        } catch (error: any) {
            console.error('Error adding transaction:', error.response?.data || error.message);
            setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error adding transaction.');
        }
    };

    const handleEditTransaction = (transaction: FinancialRecord) => {
        setEditingTransaction(transaction);
    };

    const handleDeleteTransaction = async (transactionId: string) => {
        showConfirm('Confirm Action', 'Are you sure you want to delete this transaction?', async () => {
            try {
                await api.delete(`/financials/${transactionId}`);
                setMessage('Transaction deleted successfully!');
                dispatchFinancialsChangeEvent();
            } catch (error: any) {
                console.error('Error deleting transaction:', error);
                setMessage(error.response?.data?.detail || 'Failed to delete transaction.');
            }
        });
    };

    const handleCloseModal = () => {
        setEditingTransaction(null);
    };

    if (loading) {
        return <div>{t("financialsLedger.k1")}</div>;
    }

    if (error) {
        return <div className="text-danger">{t("financialsLedger.k2")} {error}</div>;
    }

    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

    const renderTable = (records: FinancialRecord[], type: 'income' | 'expense') => (
        <div className="overflow-x-auto bg-bg-secondary p-4 rounded-lg shadow border border-border-primary mb-6">
            <h3 className="text-xl font-bold mb-3 text-text-secondary">{type === 'income' ? t("financialsLedger.k3") : t("financialsLedger.k4")}</h3>
            {records.length === 0 ? (
                <p className="text-text-secondary">{t("financialsLedger.k5")} {type} {t("financialsLedger.k6")}</p>
            ) : (
                <table className="min-w-full divide-y divide-border-primary">
                    <thead className="bg-bg-tertiary">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k7")}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k8")}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k9")}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k10")}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k11")}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{t("financialsLedger.k12")}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-bg-secondary divide-y divide-border-primary">
                        {records.map((record) => (
                            <tr key={record.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">{record.transaction_type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{record.description || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{formatCurrency(record.amount)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{record.due_date || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{record.paid_date || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button onClick={() => handleEditTransaction(record)} className="text-indigo-600 hover:text-indigo-900 mr-4">{t("financialsLedger.k13")}</button>
                                    <button onClick={() => handleDeleteTransaction(record.id!)} className="text-red-600 hover:text-red-900">{t("financialsLedger.k14")}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );

    return (
        <div className="mt-8">
            <EditTransactionModal transaction={editingTransaction} onClose={handleCloseModal} />
            <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("financialsLedger.k15")}</h2>
            {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}
            <form onSubmit={handleAddTransaction} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
                <div>
                    <label htmlFor="transactionTypeCategory" className="block text-sm font-medium text-text-secondary mb-1">{t("financialsLedger.k16")}</label>
                    <select
                        id="transactionTypeCategory"
                        name="transactionTypeCategory"
                        value={transactionTypeCategory}
                        onChange={(e) => setTransactionTypeCategory(e.target.value as 'income' | 'expense')}
                        className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    >
                        <option value="income">{t("financialsLedger.k3")}</option>
                        <option value="expense">{t("financialsLedger.k17")}</option>
                    </select>
                </div>
                <input type="text" name="transaction_type" placeholder={t("financialsLedger.k18")} value={newTransaction.transaction_type || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                <input type="text" name="description" placeholder={t("financialsLedger.k8")} value={newTransaction.description || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
                <input type="number" name="amount" placeholder={t("financialsLedger.k9")} value={newTransaction.amount !== undefined && newTransaction.amount !== null ? String(newTransaction.amount).replace(/[^0-9.]/g, '') : ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
                <div>
                    <label htmlFor="due_date" className="block text-sm font-medium text-text-secondary mb-1">{t("financialsLedger.k10")}</label>
                    <input type="date" id="due_date" name="due_date" value={newTransaction.due_date || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
                </div>
                <div>
                    <label htmlFor="paid_date" className="block text-sm font-medium text-text-secondary mb-1">{t("financialsLedger.k11")}</label>
                    <input type="date" id="paid_date" name="paid_date" value={newTransaction.paid_date || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
                </div>
                <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">{t("financialsLedger.k19")}</button>
            </form>

            <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("financialsLedger.k20")}</h2>
            {renderTable(financials.income, 'income')}
            {renderTable(financials.expenses, 'expense')}
        </div>
    );
};

export default FinancialsLedger;
